import { apiFetch } from './client';
import { DEFAULT_ARTIST_ID } from '../../config/nova-constants';

// Types for artist analytics
export interface ArtistOverviewData {
  platform: string;
  title: string;
  subtitle: string;
  currentStreams: number;
  previousStreams: number;
  percentage: string;
  isPositive: boolean;
  error?: string;
  allMetrics?: {
    type: string;
    value: number;
    weeklyDiff: number;
    monthlyDiff: number;
  };
}

export interface ArtistInfo {
  id: string;
  name: string;
  pronoun?: string;
  imageUrl?: string;
  countryCode?: string;
  genres?: string;
  band?: boolean;
  signed?: boolean;
  dateRecorded?: string;
  dateUpdated?: string;
  source: string;
}

export interface UserArtist {
  id: string;
  name: string;
  role: 'owner' | 'manager' | 'collaborator';
  isPrimary: boolean;
  permissions: string[];
  pronoun?: string;
  imageUrl?: string;
  countryCode?: string;
  genres?: string;
  band?: boolean;
  signed?: boolean;
  createdAt?: string;
  isDefault?: boolean; // True if this is the default artist (48718)
}

export interface StreamingData {
  period: string;
  streams: number;
  unique_listeners: number;
}

export interface RevenueData {
  date: string;
  daily_revenue: number;
  platform: string;
}

export interface DashboardSummary {
  totalStreams: number;
  uniqueListeners: number;
  topCountries: Array<{ country: string; streams: number }>;
  totalRevenue: number | string; // Can be "Access Denied" for collaborators
}

// API Response interfaces
export interface ArtistOverviewResponse {
  data: ArtistOverviewData[];
  artist: ArtistInfo;
  userRole: string;
  permissions: string[];
}

export interface UserArtistsResponse {
  artists: UserArtist[];
}

export interface StreamsResponse {
  data: StreamingData[];
  artistId: string;
  filters: {
    platform?: string;
    days: number;
    groupBy: string;
  };
}

export interface RevenueResponse {
  data: RevenueData[];
  artistId: string;
  period: string;
}

export interface DashboardResponse {
  data: DashboardSummary;
  artistId: string;
  period: string;
  userRole: string;
}

export interface MonthlyListenersChart {
  xAxisData: string[];
  series: Array<{
    name: string;
    type: 'line';
    data: number[];
    smooth: boolean;
    lineStyle: {
      color: string;
      width: number;
    };
    itemStyle: {
      color: string;
    };
    symbol: string;
    symbolSize: number;
  }>;
}

export interface MonthlyListenersTable {
  platform: string;
  color: string;
  monthlyData: { [month: string]: number };
  overallGrowth: string;
  isPositive: boolean;
}

export interface MonthlyListenersData {
  chart: MonthlyListenersChart;
  table: MonthlyListenersTable[];
  totalValue: number;
  months: number;
}

export interface MonthlyMetricsData {
  listeners: MonthlyListenersData;
  followers: MonthlyListenersData;
  views: MonthlyListenersData;
  youtubeViews: MonthlyListenersData;
}

export interface MonthlyListenersResponse {
  data: MonthlyMetricsData;
  artistId: string;
  artistName: string;
  period: string;
}

/**
 * Artist Analytics Service
 * Handles all API calls related to artist analytics with role-based access
 */
export const artistAnalyticsService = {
  /**
   * Get artist overview analytics (DSP streams, growth)
   * @param artistId Optional specific artist ID, uses primary artist if not provided
   * @param startDate Optional start date filter (YYYY-MM-DD format)
   * @param endDate Optional end date filter (YYYY-MM-DD format)
   */
  getArtistOverview: async (artistId?: string, startDate?: string, endDate?: string): Promise<ArtistOverviewResponse> => {
    const params = new URLSearchParams();
    
    if (artistId) params.append('artist_id', artistId);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    const queryString = params.toString();
    return apiFetch(`/analytics/artist/overview${queryString ? '?' + queryString : ''}`);
  },

  /**
   * Get detailed streaming analytics
   * @param options Filtering options
   */
  getStreamingAnalytics: async (options: {
    artistId?: string;
    platform?: string;
    days?: number;
    groupBy?: 'hour' | 'day' | 'week' | 'month';
  } = {}): Promise<StreamsResponse> => {
    const params = new URLSearchParams();
    
    if (options.artistId) params.append('artist_id', options.artistId);
    if (options.platform) params.append('platform', options.platform);
    if (options.days) params.append('days', options.days.toString());
    if (options.groupBy) params.append('groupBy', options.groupBy);
    
    const queryString = params.toString();
    return apiFetch(`/analytics/artist/streams${queryString ? '?' + queryString : ''}`);
  },

  /**
   * Get revenue analytics (owner/manager only)
   * @param artistId Optional artist ID
   * @param days Number of days to look back
   */
  getRevenueAnalytics: async (artistId?: string, days: number = 30): Promise<RevenueResponse> => {
    const params = new URLSearchParams();
    
    if (artistId) params.append('artist_id', artistId);
    params.append('days', days.toString());
    
    const queryString = params.toString();
    return apiFetch(`/analytics/artist/revenue${queryString ? '?' + queryString : ''}`);
  },

  /**
   * Get list of user's artists with roles and permissions
   */
  getUserArtists: async (): Promise<UserArtistsResponse> => {
    return apiFetch('/analytics/artist/user/artists');
  },

  /**
   * Get user's primary artist ID (or default if no mapping exists)
   */
  getUserPrimaryArtistId: async (): Promise<{ artistId: string; isDefault: boolean }> => {
    const response = await artistAnalyticsService.getUserArtists();
    const primaryArtist = response.artists.find(artist => artist.isPrimary) || response.artists[0];
    
    return {
      artistId: primaryArtist?.id || DEFAULT_ARTIST_ID,
      isDefault: primaryArtist?.isDefault || false
    };
  },

  /**
   * Get dashboard summary data
   * @param artistId Optional artist ID
   * @param days Number of days to look back
   */
  getDashboardSummary: async (artistId?: string, days: number = 30): Promise<DashboardResponse> => {
    const params = new URLSearchParams();
    
    if (artistId) params.append('artist_id', artistId);
    params.append('days', days.toString());
    
    const queryString = params.toString();
    return apiFetch(`/analytics/artist/dashboard${queryString ? '?' + queryString : ''}`);
  },

  /**
   * Get monthly listeners data for chart (Spotify, Pandora, SoundCloud)
   * @param artistId Optional artist ID
   * @param months Number of months to look back (default: 6)
   */
  getMonthlyListeners: async (artistId?: string, months: number = 6): Promise<MonthlyListenersResponse> => {
    const params = new URLSearchParams();
    
    if (artistId) params.append('artist_id', artistId);
    params.append('months', months.toString());
    
    const queryString = params.toString();
    return apiFetch(`/analytics/artist/monthly-listeners${queryString ? '?' + queryString : ''}`);
  },

  // Helper methods to extract specific metric data from the monthly listeners response
  getMonthlyListenersData: async (artistId?: string, months: number = 6): Promise<{ data: MonthlyListenersData; artistId: string; artistName: string; period: string; }> => {
    const response = await artistAnalyticsService.getMonthlyListeners(artistId, months);
    return {
      data: response.data.listeners,
      artistId: response.artistId,
      artistName: response.artistName,
      period: response.period
    };
  },

  getMonthlyFollowersData: async (artistId?: string, months: number = 6): Promise<{ data: MonthlyListenersData; artistId: string; artistName: string; period: string; }> => {
    const response = await artistAnalyticsService.getMonthlyListeners(artistId, months);
    return {
      data: response.data.followers,
      artistId: response.artistId,
      artistName: response.artistName,
      period: response.period
    };
  },

  getYouTubeViewsData: async (artistId?: string, months: number = 6): Promise<{ data: MonthlyListenersData; artistId: string; artistName: string; period: string; }> => {
    const response = await artistAnalyticsService.getMonthlyListeners(artistId, months);
    return {
      data: response.data.youtubeViews,
      artistId: response.artistId,
      artistName: response.artistName,
      period: response.period
    };
  },

  /**
   * Check if user has specific permission for current artist
   * @param permissions User's permissions array
   * @param requiredPermission Permission to check
   */
  hasPermission: (permissions: string[], requiredPermission: string): boolean => {
    return permissions.includes(requiredPermission);
  },

  /**
   * Get user-friendly role display name
   * @param role User's role
   */
  getRoleDisplayName: (role: string): string => {
    switch (role) {
      case 'owner':
        return 'Owner';
      case 'manager':
        return 'Manager';
      case 'collaborator':
        return 'Collaborator';
      default:
        return role.charAt(0).toUpperCase() + role.slice(1);
    }
  },

  /**
   * Get role badge color for UI
   * @param role User's role
   */
  getRoleBadgeColor: (role: string): string => {
    switch (role) {
      case 'owner':
        return 'bg-green-600 text-green-100';
      case 'manager':
        return 'bg-blue-600 text-blue-100';
      case 'collaborator':
        return 'bg-gray-600 text-gray-100';
      default:
        return 'bg-gray-500 text-gray-100';
    }
  },

  /**
   * Format streaming numbers for display
   * @param streams Number of streams
   */
  formatStreams: (streams: number): string => {
    if (streams >= 1000000) {
      return `${(streams / 1000000).toFixed(1)}M`;
    } else if (streams >= 1000) {
      return `${(streams / 1000).toFixed(1)}K`;
    }
    return streams.toString();
  },

  /**
   * Format revenue for display
   * @param revenue Revenue amount
   */
  formatRevenue: (revenue: number | string): string => {
    if (typeof revenue === 'string') {
      return revenue; // e.g., "Access Denied"
    }
    
    if (revenue >= 1000000) {
      return `$${(revenue / 1000000).toFixed(2)}M`;
    } else if (revenue >= 1000) {
      return `$${(revenue / 1000).toFixed(1)}K`;
    }
    return `$${revenue.toFixed(2)}`;
  },

  /**
   * Get editorial playlist data for an artist
   * @param artistId Optional artist ID
   */
  getEditorialPlaylists: async (artistId?: string): Promise<EditorialPlaylistResponse> => {
    const params = new URLSearchParams();
    
    if (artistId) params.append('artist_id', artistId);
    
    const queryString = params.toString();
    return apiFetch(`/analytics/artist/editorial-playlists${queryString ? '?' + queryString : ''}`);
  },

  /**
   * Get geographic distribution of listeners from secondary database
   * @param clickhouseArtistId ClickHouse artist ID (required for secondary database queries)
   */
  getGeographicDistribution: async (clickhouseArtistId?: string): Promise<GeographicDistributionResponse> => {
    const effectiveClickhouseArtistId = clickhouseArtistId || DEFAULT_ARTIST_ID;
    return apiFetch(`/multi-analytics/geographic-distribution/${effectiveClickhouseArtistId}`);
  },

  /**
   * Get total playlist reach data (editorial + user generated)
   * @param artistId Optional artist ID
   */
  getTotalPlaylistReach: async (artistId?: string): Promise<TotalPlaylistReachResponse> => {
    const params = new URLSearchParams();
    
    if (artistId) params.append('artist_id', artistId);
    
    const queryString = params.toString();
    return apiFetch(`/analytics/artist/total-playlist-reach${queryString ? '?' + queryString : ''}`);
  },

  /**
   * Get audience reach pyramid data
   * @param artistId Optional artist ID
   */
  getAudienceReachPyramid: async (artistId?: string): Promise<AudienceReachPyramidResponse> => {
    const params = new URLSearchParams();
    
    if (artistId) params.append('artist_id', artistId);
    
    const queryString = params.toString();
    return apiFetch(`/analytics/artist/audience-reach-pyramid${queryString ? '?' + queryString : ''}`);
  },

  /**
   * Get platform growth data (weekly and monthly growth)
   * @param artistId Optional artist ID
   */
  getPlatformGrowthData: async (artistId?: string): Promise<PlatformGrowthResponse> => {
    const params = new URLSearchParams();
    
    if (artistId) params.append('artist_id', artistId);
    
    const queryString = params.toString();
    return apiFetch(`/analytics/artist/platform-growth${queryString ? '?' + queryString : ''}`);
  },

  /**
   * Get Spotify funnel data from secondary database (cm_artist_cache table)
   * @param clickhouseArtistId ClickHouse artist ID (required for secondary database queries)
   */
  getSpotifyFunnelData: async (clickhouseArtistId?: string): Promise<SpotifyFunnelResponse> => {
    const effectiveClickhouseArtistId = clickhouseArtistId || DEFAULT_ARTIST_ID;
    return apiFetch(`/multi-analytics/spotify-funnel/${effectiveClickhouseArtistId}`);
  }
};

// Editorial Playlist Types
export interface EditorialPlaylistPieData {
  name: string;
  value: number;
  itemStyle: {
    color: string;
  };
}

export interface EditorialPlaylistTableRow {
  platform: string;
  editorialPlaylists: string;
  editorialReach: string;
  avgReachPerEditorial: string;
  color: string;
  rawEditorialReach: number;
  rawEditorialPlaylists: number;
  rawAvgReach: number;
}

export interface EditorialPlaylistData {
  pieChart: {
    data: EditorialPlaylistPieData[];
    total: {
      playlists: number;
      reach: number;
      formattedPlaylists: string;
      formattedReach: string;
    };
  };
  table: EditorialPlaylistTableRow[];
  summary: {
    totalEditorialPlaylists: number;
    totalEditorialReach: number;
    platformCount: number;
  };
}

export interface EditorialPlaylistResponse {
  data: EditorialPlaylistData;
  artistId: string;
  artistName: string;
}

// Geographic Distribution Types
export interface GeographicDistributionData {
  country_code: string;
  name: string;
  value: number;
  spotify_listeners?: number;
  youtube_views?: number;
  cities_count: number;
  top_city: string;
  top_city_listeners: number;
  formatted_value: string;
}

export interface GeographicDistributionResponse {
  data: GeographicDistributionData[];
  artistId: string;
  artistName: string;
  totalListeners: number;
}

// Total Playlist Reach Types
export interface TotalPlaylistReachData {
  platform_name: string;
  total_tracks: number; // Keep for backend calculations but not displayed
  total_playlists: number;
  total_reach: number;
  avg_reach_per_track: number;
  formatted_reach: string;
  formatted_playlists: string;
}

export interface TotalPlaylistReachResponse {
  data: TotalPlaylistReachData[];
  artistId: string;
  artistName: string;
  totalReach: number;
  totalPlaylists: number;
}

// Audience Reach Pyramid Types
export interface AudienceReachPyramidData {
  name: string;
  value: number;
  color: string;
  platforms_detail: string;
  metrics_count: number;
  formatted_audience: string;
  tier_description: string;
  avg_per_metric: number;
}

export interface AudienceReachPyramidResponse {
  data: AudienceReachPyramidData[];
  artistId: string;
  artistName: string;
  totalMetrics: number;
  totalAudience: number;
}

// Platform Growth Types
export interface PlatformGrowthData {
  platform: string;
  metric_type: string;
  current_value: number;
  weekly_growth: number;
  monthly_growth: number;
  weekly_growth_rate: number;
  monthly_growth_rate: number;
  daily_growth_velocity: number;
  timestamp: string;
}

export interface PlatformGrowthResponse {
  data: PlatformGrowthData[];
  artistId: string;
  artistName: string;
  totalPlatforms: number;
}

// Spotify Funnel Types
export interface SpotifyFunnelData {
  playlistTotalReach: number;
  monthlyListeners: number;
  followers: number;
}

export interface SpotifyFunnelResponse {
  data: SpotifyFunnelData;
  platformArtistId: string;
  artistName: string;
}

export default artistAnalyticsService;